import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../baseQuery';

interface LoginRequest {
  email: string;
  password: string;
}

interface RegisterRequest {
  email: string;
  username: string;
  password: string;
}

interface SaveAddressRequest {
  firstName: string;
  lastName: string;
  address1: string;
  address2?: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

export interface LoginResponse {
  token: string;
  message: string;
  csrfToken?: string; // New CSRF token from backend
  user?: {
    id: number;
    email: string;
    username: string;
    roles: string[]; // Backend RBAC system - user roles from database
  };
}

export interface RegisterResponse {
  message: string;
  user: {
    id: number;
    email: string;
    username: string;
    createdAt: string;
    roles?: string[]; // Backend RBAC system - new users get ["user"] role
  };
  token: string;
}

export interface SaveAddressResponse {
  message: string;
  user: {
    id: number;
    email: string;
    username: string;
    shippingFirstName: string;
    shippingLastName: string;
    shippingAddress1: string;
    shippingAddress2?: string;
    shippingCity: string;
    shippingState: string;
    shippingPostalCode: string;
    shippingCountry: string;
    updatedAt: string;
  };
}

export interface ProfileData {
  id: number;
  email: string;
  username: string;
  phoneNumber?: string;
  phone?: string; // For backwards compatibility
  // New nested structure from backend
  shippingAddress?: {
    firstName?: string;
    lastName?: string;
    address1?: string;
    address2?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    country?: string;
  };
  // Old flat structure for backwards compatibility
  shippingFirstName?: string;
  shippingLastName?: string;
  shippingAddress1?: string;
  shippingAddress2?: string;
  shippingCity?: string;
  shippingState?: string;
  shippingPostalCode?: string;
  shippingCountry?: string;
  createdAt?: string;
  updatedAt: string;
}

export interface UpdateProfileRequest {
  username: string;
  firstName: string;
  lastName: string;
  address1: string;
  address2?: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  phone?: string;
}

export interface UpdateProfileResponse {
  message: string;
  user: ProfileData;
}

// Password Reset Types
export interface ForgotPasswordRequest {
  email: string;
}

export interface ForgotPasswordResponse {
  message: string;
}

export interface ValidateResetTokenResponse {
  valid: boolean;
  email?: string; // Masked email
}

export interface ResetPasswordRequest {
  token: string;
  newPassword: string;
}

export interface ResetPasswordResponse {
  token: string;
  csrfToken?: string;
  message: string;
  user: {
    id: number;
    email: string;
    username: string;
    roles: string[];
  };
}

// Email Change Types
export interface RequestEmailChangeRequest {
  newEmail: string;
}

export interface RequestEmailChangeResponse {
  message: string;
  expiresAt: string;
}

export interface EmailChangeStatusResponse {
  pendingEmail?: string;
  requestedAt?: string;
  expiresAt?: string;
}

export interface ResendEmailVerificationResponse {
  message: string;
}

export interface CancelEmailChangeResponse {
  message: string;
}

export interface VerifyEmailChangeResponse {
  message: string;
  email?: string; // New email address that was verified
  user?: {
    id: number;
    email: string;
    username: string;
  };
}

// Account Email Verification Types (initial verification)
export interface VerifyEmailResponse {
  message: string;
  userId?: number;
  email?: string;
  csrfToken?: string;
}

export interface ResendVerificationResponse {
  message: string;
}

export interface VerificationStatusResponse {
  emailVerified: boolean;
  emailVerifiedAt?: string;
  hasPendingVerification?: boolean;
}

// Phone Verification Types
export interface SendPhoneVerificationRequest {
  phoneNumber: string; // Phone number without country code
  countryCode?: string; // Optional country code (defaults to +1)
}

export interface SendPhoneVerificationResponse {
  message: string;
  verificationSid: string;
  expiresAt: string;
}

export interface VerifyPhoneRequest {
  phoneNumber: string;
  code: string;
  verificationSid: string;
}

export interface VerifyPhoneResponse {
  success: boolean;
  message: string;
  phoneVerified: boolean;
  token?: string; // New JWT token if updated
  csrfToken?: string; // New CSRF token if updated
}

export interface PhoneVerificationStatusResponse {
  phoneNumber?: string;
  phoneVerified: boolean;
  phoneVerifiedAt?: string;
}

export const authApi = createApi({
  reducerPath: 'authApi',
  baseQuery: baseQuery,
  endpoints: (builder) => ({
    logout: builder.mutation<{ message: string }, void>({
      query: () => ({
        url: '/auth/logout',
        method: 'POST',
      }),
    }),
    login: builder.mutation<LoginResponse, LoginRequest>({
      query: (credentials) => {
        console.log('🚀 Login API Request:', {
          url: '/auth/login',
          email: credentials.email,
          passwordLength: credentials.password.length
        });
        return {
          url: '/auth/login',
          method: 'POST',
          body: credentials,
        };
      },
    }),
    register: builder.mutation<RegisterResponse, RegisterRequest>({
      query: (credentials) => {
        console.log('🚀 Registration API Request:', {
          url: '/auth/beta-register',
          email: credentials.email,
          username: credentials.username,
          passwordLength: credentials.password.length,
        });
        return {
          url: '/auth/beta-register',
          method: 'POST',
          body: credentials,
        };
      },
    }),
    saveAddress: builder.mutation<SaveAddressResponse, SaveAddressRequest>({
      query: (addressData) => {
        console.log('🏠 Save Address API Request:', {
          url: '/users/address',
          addressData,
        });
        
        return {
          url: '/users/address',
          method: 'POST',
          body: addressData,
          requiresAuth: true,
        };
      },
    }),
    getProfile: builder.query<ProfileData, void>({
      query: () => {
        console.log('👤 Get Profile API Request:', {
          url: '/users/profile'
        });
        
        return {
          url: '/users/profile',
          method: 'GET',
          requiresAuth: true,
        };
      },
    }),
    updateProfile: builder.mutation<UpdateProfileResponse, UpdateProfileRequest>({
      query: (profileData) => {
        console.log('✏️ Update Profile API Request:', {
          url: '/users/profile',
          profileData,
        });
        
        return {
          url: '/users/profile',
          method: 'PUT',
          body: profileData,
          requiresAuth: true,
        };
      },
    }),
    forgotPassword: builder.mutation<ForgotPasswordResponse, ForgotPasswordRequest>({
      query: (data) => {
        console.log('🔑 Forgot Password API Request:', {
          url: '/auth/forgot-password',
          email: data.email,
        });
        
        return {
          url: '/auth/forgot-password',
          method: 'POST',
          body: data,
        };
      },
    }),
    resetPassword: builder.mutation<ResetPasswordResponse, ResetPasswordRequest>({
      query: (data) => {
        console.log('🔐 Reset Password API Request:', {
          url: '/auth/reset-password',
          token: data.token,
        });
        
        return {
          url: '/auth/reset-password',
          method: 'POST',
          body: data,
        };
      },
    }),
    validateResetToken: builder.query<ValidateResetTokenResponse, string>({
      query: (token) => {
        console.log('✅ Validate Reset Token API Request:', {
          url: `/auth/validate-reset-token/${token}`,
        });

        return {
          url: `/auth/validate-reset-token/${token}`,
          method: 'GET',
        };
      },
    }),
    requestEmailChange: builder.mutation<RequestEmailChangeResponse, RequestEmailChangeRequest>({
      query: (data) => {
        console.log('📧 Request Email Change API Request:', {
          url: '/auth/request-email-change',
          newEmail: data.newEmail,
        });

        return {
          url: '/auth/request-email-change',
          method: 'POST',
          body: data,
          requiresAuth: true,
        };
      },
    }),
    getEmailChangeStatus: builder.query<EmailChangeStatusResponse, void>({
      query: () => {
        console.log('📊 Get Email Change Status API Request:', {
          url: '/auth/email-change-status',
        });

        return {
          url: '/auth/email-change-status',
          method: 'GET',
          requiresAuth: true,
        };
      },
    }),
    resendEmailVerification: builder.mutation<ResendEmailVerificationResponse, void>({
      query: () => {
        console.log('🔄 Resend Email Verification API Request:', {
          url: '/auth/resend-email-change',
        });

        return {
          url: '/auth/resend-email-change',
          method: 'POST',
          requiresAuth: true,
        };
      },
    }),
    cancelEmailChange: builder.mutation<CancelEmailChangeResponse, void>({
      query: () => {
        console.log('❌ Cancel Email Change API Request:', {
          url: '/auth/email-change',
        });

        return {
          url: '/auth/email-change',
          method: 'DELETE',
          requiresAuth: true,
        };
      },
    }),
    verifyEmailChange: builder.query<VerifyEmailChangeResponse, string>({
      query: (token) => {
        console.log('✅ Verify Email Change API Request:', {
          url: `/auth/verify-email-change/${token}`,
        });

        return {
          url: `/auth/verify-email-change/${token}`,
          method: 'GET',
        };
      },
    }),
    // Account Email Verification endpoints (initial verification)
    verifyEmail: builder.query<VerifyEmailResponse, string>({
      query: (token) => {
        console.log('✅ Verify Email API Request:', {
          url: `/auth/verify-email/${token}`,
        });

        return {
          url: `/auth/verify-email/${token}`,
          method: 'GET',
        };
      },
    }),
    resendVerification: builder.mutation<ResendVerificationResponse, void>({
      query: () => {
        console.log('🔄 Resend Verification API Request:', {
          url: '/auth/resend-verification',
        });

        return {
          url: '/auth/resend-verification',
          method: 'POST',
          requiresAuth: true,
        };
      },
    }),
    getVerificationStatus: builder.query<VerificationStatusResponse, void>({
      query: () => {
        console.log('📊 Get Verification Status API Request:', {
          url: '/auth/verification-status',
        });

        return {
          url: '/auth/verification-status',
          method: 'GET',
          requiresAuth: true,
        };
      },
    }),
    // Phone Verification endpoints
    sendVerificationCode: builder.mutation<SendPhoneVerificationResponse, SendPhoneVerificationRequest>({
      query: (data) => {
        console.log('📱 Send Phone Verification Code API Request:', {
          url: '/auth/send-verification-code',
          phoneNumber: data.phoneNumber,
        });

        return {
          url: '/auth/send-verification-code',
          method: 'POST',
          body: data,
          requiresAuth: true,
        };
      },
    }),
    verifyPhone: builder.mutation<VerifyPhoneResponse, VerifyPhoneRequest>({
      query: (data) => {
        console.log('✅ Verify Phone API Request:', {
          url: '/auth/verify-phone',
          phoneNumber: data.phoneNumber,
          verificationSid: data.verificationSid,
        });

        return {
          url: '/auth/verify-phone',
          method: 'POST',
          body: data,
          requiresAuth: true,
        };
      },
    }),
    getPhoneVerificationStatus: builder.query<PhoneVerificationStatusResponse, void>({
      query: () => {
        console.log('📊 Get Phone Verification Status API Request:', {
          url: '/auth/phone-verification-status',
        });

        return {
          url: '/auth/phone-verification-status',
          method: 'GET',
          requiresAuth: true,
        };
      },
    }),
  }),
});

export const {
  useLogoutMutation,
  useLoginMutation,
  useRegisterMutation,
  useSaveAddressMutation,
  useGetProfileQuery,
  useUpdateProfileMutation,
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useValidateResetTokenQuery,
  useRequestEmailChangeMutation,
  useGetEmailChangeStatusQuery,
  useResendEmailVerificationMutation,
  useCancelEmailChangeMutation,
  useVerifyEmailChangeQuery,
  useVerifyEmailQuery,
  useResendVerificationMutation,
  useGetVerificationStatusQuery,
  useSendVerificationCodeMutation,
  useVerifyPhoneMutation,
  useGetPhoneVerificationStatusQuery
} = authApi;
